/**
 * 家长录音（覆盖 TTS）的**体积与格式口径** —— 前后端共用。
 *
 * ## 为什么要有"体积判定"这么一个小函数
 *
 * 本项目**没有文件存储**（没有 multer、没有上传卷、worker 也不写文件），所以
 * 录音以 **base64 存进 Postgres**（表 `dictation_word_audio`，理由见
 * `packages/db/src/schema.ts` 的注释与 `docs/接口契约对齐.md`）。
 * 这条路唯一的真实风险是**体积失控**：一个 `MediaRecorder` 忘了 `stop()`，
 * 或者家长按着录了 5 分钟，就会变成一个 1 MB 的 JSON —— 而 API 的
 * `bodyLimit` 正好是 1 MB，于是请求被 Fastify **在路由之前**挡掉，
 * 家长看到的是一句笼统的"请求格式有误"，完全不知道是"录音太长"。
 *
 * 所以判定必须**在浏览器里、上传之前**做，而且要用**与后端同一条上限**：
 *   · 界面在录制过程中实时显示大小与上限；
 *   · 超过上限 → 禁用「保存」并直接说"太长了，重录一段"（不是等上传失败）；
 *   · 接近上限（80%）→ 黄色提醒"这段有点长"。
 *
 * ## 单位口径（要说清楚，否则又是一个"0 与拿不到分不清"）
 *
 * | 量 | 出处 | 含义 |
 * | --- | --- | --- |
 * | `byteSize` | 浏览器 `Blob.size` | **原始音频字节数**（真正占多少空间） |
 * | `base64.length` | 编码后字符串长度 | 数据库里那一列的长度（体积判定用它） |
 *
 * 两者差约 4/3 倍，**必须分开存、分开比**：`ck_dictation_word_audio_size` 约束的是
 * **base64 字符数**（≤ 400 000），而界面给家长看的是"这段录音多少 KB"（原始字节）。
 * 用其中任何一个去糊弄另一个，都会在边界上错得很难查。
 */

/** base64 字符串长度上限 —— 与 `ck_dictation_word_audio_size`（400000）**逐字一致**。 */
export const DICTATION_AUDIO_MAX_BASE64 = 400_000;

/**
 * base64 长度超过它就提醒"有点长"（上限的 75%）。
 *
 * 400 000 字符 ≈ 300 KB 音频。opus 在 24 kbps 下约 3 KB/s，所以 300 KB ≈ **100 秒**；
 * 但浏览器默认常常是 128 kbps 的 opus 容器（约 16 KB/s），那 300 KB 只有 **约 19 秒**。
 * 所以界面必须显示**预计可录多少秒**（用真实码率算，见 `describeAudioBudget`），
 * 而不是写死一句"最多 20 秒"（在某些浏览器上那是错的）。
 */
export const DICTATION_AUDIO_WARN_BASE64 = 300_000;

/** 单个词建议的录音时长上限（毫秒）。超过就提醒重录更短的一段。 */
export const DICTATION_AUDIO_SUGGESTED_MAX_MS = 20_000;

/** base64 字符数 → 解码后的字节数（按 4 字符 3 字节估，末尾填充修正）。 */
export function base64DecodedBytes(base64Length: number): number {
  if (!Number.isFinite(base64Length) || base64Length <= 0) return 0;
  const len = Math.floor(base64Length);
  const padding = len % 4 === 0 ? 0 : 4 - (len % 4);
  return Math.floor((len * 3) / 4) - padding;
}

/** 录音是否合法（体积判定）。`code` 供界面直接选文案。 */
export interface AudioSizeVerdict {
  /** `ok` 可以保存 / `warn` 能保存但提醒 / `error` 必须重录。 */
  code: 'ok' | 'warn' | 'error';
  /** 原始字节数。 */
  bytes: number;
  /** base64 字符数。 */
  base64Length: number;
  /** 上限（base64 字符数），用于界面显示"还差多少"。 */
  maxBase64Length: number;
  /** `error` 时：超出上限多少字符（否则 0）。 */
  overBy: number;
  /** 给家长看的一句话（永远是中文人话）。 */
  message: string;
}

/**
 * 判定一段录音能不能存。
 *
 * `bytes` 由调用方给（浏览器 `Blob.size`）—— 这里**不**从 base64 反算，
 * 因为 base64 长度只是"约等于"原始大小，而 Blob 给的是准数。
 */
export function classifyAudioSize(input: {
  base64Length: number;
  bytes: number;
}): AudioSizeVerdict {
  const base64Length = Number.isFinite(input.base64Length) ? Math.floor(input.base64Length) : 0;
  const bytes = Number.isFinite(input.bytes) ? Math.floor(input.bytes) : 0;
  const base = { bytes, base64Length, maxBase64Length: DICTATION_AUDIO_MAX_BASE64 };

  if (base64Length <= 0) {
    return {
      ...base,
      code: 'error',
      overBy: 0,
      // 空录音是**失败**，不是"说了 0 秒"：MediaRecorder 出错时正好会给出空 blob
      message: '这一段没有录到声音，请重新录一遍。',
    };
  }
  if (base64Length > DICTATION_AUDIO_MAX_BASE64) {
    return {
      ...base,
      code: 'error',
      overBy: base64Length - DICTATION_AUDIO_MAX_BASE64,
      message: `这段录音太大了（${formatBytes(bytes)}，上限 ${formatBytes(
        base64DecodedBytes(DICTATION_AUDIO_MAX_BASE64),
      )}）—— 请把每个词的录音控制在很短的一段里。`,
    };
  }
  if (base64Length >= DICTATION_AUDIO_WARN_BASE64) {
    return {
      ...base,
      code: 'warn',
      overBy: 0,
      message: `这段录音有点长（${formatBytes(bytes)}），已经接近上限了。`,
    };
  }
  return { ...base, code: 'ok', overBy: 0, message: '' };
}

/** 字节数 → 人话（`312 KB` / `1.2 MB`）。`0` 就是 `0 B`（不是「—」：这是确定的值）。 */
export function formatBytes(bytes: number | null | undefined): string {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * 「按这个码率，你还能录大约多少秒」。
 *
 * `bitsPerSecond` 来自真实录音（`blob.size * 8 / 已录秒数`），所以不同浏览器
 * 给出的答案不同 —— 这正是不能写死"最多 20 秒"的原因。
 * 码率未知（还在录、时长太短）时返回 `null`（= 还算不出来，界面显示「—」，
 * **不是 0**）。
 */
export function estimateRecordableSeconds(
  bitsPerSecond: number | null | undefined,
  remainingBase64 = DICTATION_AUDIO_MAX_BASE64,
): number | null {
  if (
    typeof bitsPerSecond !== 'number' ||
    !Number.isFinite(bitsPerSecond) ||
    bitsPerSecond <= 0 ||
    remainingBase64 <= 0
  ) {
    return null;
  }
  const remainingBytes = base64DecodedBytes(remainingBase64);
  return Math.floor((remainingBytes * 8) / bitsPerSecond);
}

/** 录音格式的中文标签（界面上的"录音格式"一行）。认不出来就原样显示。 */
export function audioFormatLabel(mimeType: string | null | undefined): string {
  if (typeof mimeType !== 'string' || mimeType === '') return '—';
  const base = mimeType.split(';')[0]!.trim().toLowerCase();
  const known: Record<string, string> = {
    'audio/webm': 'WebM',
    'audio/ogg': 'Ogg',
    'audio/mp4': 'MP4/AAC',
    'audio/mpeg': 'MP3',
    'audio/wav': 'WAV',
    'audio/x-wav': 'WAV',
    'audio/aac': 'AAC',
  };
  return known[base] ?? base;
}

/** 这个 mime 是不是浏览器能**播放**的（用于上传前的自检与错误提示）。 */
export function isPlayableAudioMime(mimeType: string | null | undefined): boolean {
  if (typeof mimeType !== 'string' || mimeType === '') return false;
  return /^audio\/[a-z0-9.+-]+/i.test(mimeType);
}

/** `data:` URL（拼给 `<audio src>`；base64 里不会出现需要转义到 URL 的字符）。 */
export function audioDataUrl(mimeType: string, base64: string): string {
  return `data:${mimeType};base64,${base64}`;
}
